import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WearableSync = ({ devices, onSync, onConnect }) => {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    await onSync();
    setTimeout(() => setSyncing(false), 2000);
  };

  const getDeviceIcon = (type) => {
    const iconMap = {
      watch: 'Watch',
      band: 'Activity',
      scale: 'Scale',
      phone: 'Smartphone'
    };
    return iconMap?.[type] || 'Watch';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      connected: 'var(--color-success)',
      syncing: 'var(--color-warning)',
      disconnected: 'var(--color-error)'
    };
    return colorMap?.[status] || colorMap?.disconnected;
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      connected: 'Connected',
      syncing: 'Syncing...',
      disconnected: 'Disconnected'
    };
    return labelMap?.[status] || 'Unknown';
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Watch" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Wearable Devices
            </h2>
            <p className="text-sm text-muted-foreground">
              Sync your fitness data
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={handleSync}
          loading={syncing}
          disabled={syncing}
        >
          Sync All
        </Button>
      </div>
      {devices?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <Icon name="Watch" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-sm text-muted-foreground text-center mb-4">
            No devices connected
          </p>
          <Button variant="outline" iconName="Plus" iconPosition="left" onClick={onConnect}>
            Connect Device
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {devices?.map((device) => (
            <div
              key={device?.id}
              className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                  <Icon name={getDeviceIcon(device?.type)} size={24} color="var(--color-foreground)" />
                </div>

                <div>
                  <h3 className="text-base font-medium text-foreground mb-1">
                    {device?.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: getStatusColor(device?.status) }}
                    />
                    <span className="text-sm text-muted-foreground">
                      {getStatusLabel(device?.status)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">
                  Last sync
                </p>
                <p className="text-sm font-medium text-foreground">
                  {device?.lastSync}
                </p>
              </div>
            </div>
          ))}

          <Button
            variant="ghost"
            fullWidth
            iconName="Plus"
            iconPosition="left"
            onClick={onConnect}
          >
            Connect Another Device
          </Button>
        </div>
      )}
      <div className="mt-6 p-4 rounded-lg bg-muted">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground font-medium mb-1">
              Supported Devices
            </p>
            <p className="text-xs text-muted-foreground">
              Apple Watch, Fitbit, Garmin, Samsung Galaxy Watch, Google Fit, and more
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WearableSync;